<template>
  <ServicePageLayout
    icon="fas fa-search"
    title="Lost & Found"
    subtitle="Helping you recover your lost belongings"
  >
    <section class="content-section">
      <h2>
        <i class="fas fa-info-circle section-icon" />
        Overview
      </h2>
      <p>
        The Lost and Found service of OSAS helps students and staff recover items that have
        been lost on campus. Found items are collected, documented, and stored safely until
        claimed by their rightful owners.
      </p>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-question-circle section-icon" />
        How It Works
      </h2>
      <div class="steps-container">
        <div class="step-card">
          <div class="step-number">
            1
          </div>
          <h3>Item is Found</h3>
          <p>Found items are turned over to OSAS or security personnel.</p>
        </div>
        <div class="step-card">
          <div class="step-number">
            2
          </div>
          <h3>Documentation</h3>
          <p>Items are logged with description, date, and location found.</p>
        </div>
        <div class="step-card">
          <div class="step-number">
            3
          </div>
          <h3>Safe Storage</h3>
          <p>Items are stored securely in our Lost & Found area.</p>
        </div>
        <div class="step-card">
          <div class="step-number">
            4
          </div>
          <h3>Owner Claims</h3>
          <p>Owner provides proof of ownership to claim the item.</p>
        </div>
      </div>
    </section>

    <section class="content-section highlight">
      <div class="report-container">
        <div class="report-icon">
          <i class="fas fa-exclamation-triangle" />
        </div>
        <div class="report-content">
          <h3>Lost Something?</h3>
          <p>
            Report your lost item immediately to increase the chances of recovery.
            Provide as much detail as possible about the item and where you last had it.
          </p>
          <button
            class="report-btn"
            @click="reportLostItem"
          >
            <i class="fas fa-file-alt" />
            Report Lost Item Online
          </button>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-hand-holding section-icon" />
        Found Something?
      </h2>
      <p>
        If you find an item on campus, please turn it over to one of the following:
      </p>
      <div class="turnover-options">
        <div class="option">
          <i class="fas fa-building" />
          <span>OSAS Office (Room 200, Student Services Building)</span>
        </div>
        <div class="option">
          <i class="fas fa-shield-alt" />
          <span>Campus Security Office</span>
        </div>
        <div class="option">
          <i class="fas fa-concierge-bell" />
          <span>Guard on duty at any campus gate</span>
        </div>
        <div class="option">
          <i class="fas fa-door-open" />
          <span>Department/College office nearest to where item was found</span>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-box-open section-icon" />
        Recently Found Items
      </h2>
      <p class="mb-1">
        Check if your item is among those recently turned over:
      </p>
      <div
        v-if="foundItems.length > 0"
        class="items-grid"
      >
        <div
          v-for="item in foundItems"
          :key="item.id"
          class="item-card"
        >
          <div
            v-if="item.image_url"
            class="item-image"
          >
            <img
              :src="getLfImageUrl(item.image_url)"
              :alt="item.description || 'Found item'"
            >
          </div>
          <div
            v-else
            class="item-icon"
          >
            <i :class="getCategoryIcon(item.category)" />
          </div>
          <div class="item-details">
            <h4>{{ item.description }}</h4>
            <p class="item-location">
              Found: {{ item.location }}, {{ item.date_found }}
            </p>
            <p class="item-location mb-0">
              Category: {{ item.category || 'Other' }}
            </p>
            <p class="item-location mb-0">
              Status: {{ item.status || 'N/A' }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-else
        class="no-items"
      >
        <i class="fas fa-inbox" />
        <p>No recently found items to display.</p>
      </div>
      <button
        class="view-all-btn"
        @click="viewAllItems"
      >
        View All Found Items
        <i class="fas fa-arrow-right" />
      </button>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-clipboard-check section-icon" />
        How to Claim Your Item
      </h2>
      <div class="claim-steps">
        <div class="claim-step">
          <div class="claim-icon green">
            <i class="fas fa-1" />
          </div>
          <div class="claim-content">
            <h4>Visit OSAS Office</h4>
            <p>Go to Room 200, Student Services Building during office hours.</p>
          </div>
        </div>
        <div class="claim-step">
          <div class="claim-icon yellow">
            <i class="fas fa-2" />
          </div>
          <div class="claim-content">
            <h4>Provide Description</h4>
            <p>Describe the lost item in detail to the staff.</p>
          </div>
        </div>
        <div class="claim-step">
          <div class="claim-icon green">
            <i class="fas fa-3" />
          </div>
          <div class="claim-content">
            <h4>Present ID</h4>
            <p>Show your valid school ID for verification.</p>
          </div>
        </div>
        <div class="claim-step">
          <div class="claim-icon yellow">
            <i class="fas fa-4" />
          </div>
          <div class="claim-content">
            <h4>Sign Release Form</h4>
            <p>Sign the release form to complete the claiming process.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="content-section warning">
      <h2>
        <i class="fas fa-exclamation-circle section-icon" />
        Important Notes
      </h2>
      <ul class="notes-list">
        <li>Items will be kept for a maximum of 90 days.</li>
        <li>Unclaimed items may be donated to charity after the retention period.</li>
        <li>High-value items (gadgets, jewelry, cash) require additional verification.</li>
        <li>For electronic devices, you may be asked to unlock the device to prove ownership.</li>
        <li>OSAS is not liable for items not turned over to our office.</li>
      </ul>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-phone section-icon" />
        Contact Lost & Found
      </h2>
      <div class="contact-info">
        <div class="contact-item">
          <i class="fas fa-map-marker-alt" />
          <span>Room 200, Student Services Building</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-phone" />
          <span>(078) 123-4567 Local 200</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-clock" />
          <span>Monday - Friday, 8:00 AM - 5:00 PM</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-envelope" />
          <span>lostandfound@nec.edu.ph</span>
        </div>
      </div>
    </section>

    <LostItemModal
      :visible="showModal"
      :form="lostItemForm"
      :fetching="submitting"
      @close="showModal = false"
      @submit="submitLostItem"
      @error="handleError"
    />
  </ServicePageLayout>
</template>

<script>
import ServicePageLayout from '@/components/landing/ServicePageLayout.vue'
import LostItemModal from '@/components/landing/modals/LostItemModal.vue'
import { LandingService } from '@/services/landing'

export default {
  name: 'LostFoundService',

  components: {
    ServicePageLayout,
    LostItemModal
  },

  metaInfo: () => ({
    title: 'Lost & Found'
  }),

  data () {
    return {
      showModal: false,
      submitting: false,
      foundItems: [],
      lostItemForm: {
        student_name: '',
        student_id: '',
        contact_number: '',
        category: '',
        location: '',
        description: '',
        date_lost: '',
        image: null
      }
    }
  },

  mounted () {
    this.fetchFoundItems()
  },

  methods: {
    async fetchFoundItems () {
      try {
        const { data } = await LandingService.searchFoundItems()
        this.foundItems = data.data || []
      } catch {
        this.foundItems = []
      }
    },

    getCategoryIcon (category) {
      const normalized = String(category || '').toLowerCase().replace(/\s+/g, '_')
      const icons = {
        electronics: 'fas fa-laptop',
        documents: 'fas fa-id-card',
        bags: 'fas fa-shopping-bag',
        clothing: 'fas fa-tshirt',
        books: 'fas fa-book',
        keys: 'fas fa-key',
        accessories: 'fas fa-glasses',
        other: 'fas fa-box'
      }
      return icons[normalized] || 'fas fa-box'
    },

    getLfImageUrl (path) {
      if (!path || typeof path !== 'string') return ''
      const parts = path.split('/')
      const filename = parts.length ? parts[parts.length - 1] : ''
      if (!filename) return ''

      const apiBase = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '')
      return `${apiBase}/shared/uploads/lf-items/images/${encodeURIComponent(filename)}`
    },

    reportLostItem () {
      this.resetForm()
      this.showModal = true
    },

    resetForm () {
      this.lostItemForm = {
        student_name: '',
        student_id: '',
        contact_number: '',
        category: '',
        location: '',
        description: '',
        date_lost: '',
        image: null
      }
    },

    async submitLostItem () {
      // Validate required fields
      if (!this.lostItemForm.student_name || !this.lostItemForm.student_id ||
          !this.lostItemForm.contact_number || !this.lostItemForm.category ||
          !this.lostItemForm.location || !this.lostItemForm.description) {
        this.$bvToast.toast('Please fill in all required fields', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.submitting = true
      try {
        await LandingService.submitLostItem(this.lostItemForm)

        this.$bvToast.toast('Your lost item report has been submitted successfully!', {
          title: 'Report Submitted',
          variant: 'success',
          solid: true
        })
        this.showModal = false
        this.resetForm()
      } catch (error) {
        this.$bvToast.toast('Failed to submit report. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
        console.error(error)
      } finally {
        this.submitting = false
      }
    },

    handleError (message) {
      this.$bvToast.toast(message, {
        title: 'Error',
        variant: 'danger',
        solid: true
      })
    },

    viewAllItems () {
      this.$bvToast.toast('Full item listing coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>

<style scoped>
.item-image {
  width: 64px;
  height: 64px;
  border-radius: 10px;
  overflow: hidden;
  flex: 0 0 64px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f8fafc;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
</style>
